import {Breadcrumb, ButtonText, IconChevronRight} from '@aragon/ui-components';
import {IlluObject} from '@aragon/ui-components/src/components/illustrations';
import React from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import CardWithImage from 'components/cardWithImage';
import {useFormStep} from 'components/fullScreenStepper';
import {ActiveIndicator, Indicator, StyledCarousel} from 'containers/carousel';
import useScreen from 'hooks/useScreen';
import {trackEvent} from 'services/analytics';
import {i18n} from '../../../i18n.config';
import {CreateDAO} from '../../utils/paths';
import {useAuth0} from '@auth0/auth0-react';

type OverviewDAOHeaderProps = {
  navLabel: string;
  returnPath: string;
  onExitButtonClick?: () => void;
};

export const OverviewDAOHeader: React.FC<OverviewDAOHeaderProps> = ({
  navLabel,
  returnPath,
  onExitButtonClick,
}) => {
  const {t} = useTranslation();
  const {next} = useFormStep();
  const {loginWithRedirect, isAuthenticated} = useAuth0();

  const handleSetupClick = () => {
    if (!isAuthenticated) {
      return loginWithRedirect();
    }
    trackEvent('daoCreation_setupDAO_clicked');
    next();
  };

  return (
    <div className="p-2 tablet:p-6 bg-ui-0 tablet:rounded-xl">
      <div className="desktop:hidden mb-3">
        <Breadcrumb
          crumbs={{
            label: navLabel,
            path: returnPath,
          }}
          onClick={onExitButtonClick}
        />
      </div>

      <div className="tablet:flex items-end tablet:space-x-6">
        <div className="w-full">
          <h1 className="font-bold text-ui-800 ft-text-3xl">
            {t('createDAO.overview.title')}
          </h1>
          <p className="mt-2 text-ui-600 ft-text-lg">
            {t('createDAO.overview.description')}
          </p>
        </div>
        <div className="flex mt-2 tablet:mt-0 space-x-2">
          {/* <ButtonText
          size="large"
          mode="secondary"
          bgWhite
          className="whitespace-nowrap"
          label={'Continue Draft'}
        /> */}
          <ButtonText
            size="large"
            className="w-full tablet:w-max whitespace-nowrap"
            iconRight={<IconChevronRight />}
            label={!isAuthenticated ? 'Login' : t('createDAO.overview.button')}
            onClick={handleSetupClick}
          />
        </div>
      </div>
    </div>
  );
};

const OverviewCards = [
  <CardWithImage
    key="SelectBlockchain"
    imgSrc={<IlluObject object="chain" />}
    caption={i18n.t('createDAO.step1.label')}
    title={i18n.t('createDAO.step1.title')}
  />,
  <CardWithImage
    key="DefineMetadata"
    imgSrc={<IlluObject object="labels" />}
    caption={i18n.t('createDAO.step2.label')}
    title={i18n.t('createDAO.step2.title')}
  />,
  <CardWithImage
    key="SetupCommunity"
    imgSrc={<IlluObject object="users" />}
    caption={i18n.t('createDAO.step3.label')}
    title={i18n.t('createDAO.step3.title')}
  />,
  <CardWithImage
    key="ConfigureGovernance"
    imgSrc={<IlluObject object="settings" />}
    caption={i18n.t('createDAO.step4.label')}
    title={i18n.t('createDAO.step4.shortTitle')}
  />,
];

export const OverviewDAOStep: React.FC = () => {
  const {isDesktop} = useScreen();

  if (isDesktop) {
    return (
      <div className="tablet:flex space-y-3 tablet:space-y-0 tablet:space-x-3">
        {OverviewCards}
      </div>
    );
  }
  return (
    <MobileCTA>
      <StyledCarousel
        swipeable
        emulateTouch
        centerMode
        autoPlay
        preventMovementUntilSwipeScrollTolerance
        swipeScrollTolerance={100}
        interval={4000}
        showArrows={false}
        showStatus={false}
        transitionTime={300}
        centerSlidePercentage={92}
        showThumbs={false}
        infiniteLoop
        renderIndicator={(onClickHandler, isSelected, index, label) => {
          if (isSelected) {
            return (
              <ActiveIndicator
                aria-label={`Selected: ${label} ${index + 1}`}
                title={`Selected: ${label} ${index + 1}`}
              />
            );
          }
          return (
            <Indicator
              onClick={onClickHandler}
              onKeyDown={onClickHandler}
              value={index}
              key={index}
              role="button"
              tabIndex={0}
              title={`${label} ${index + 1}`}
              aria-label={`${label} ${index + 1}`}
            />
          );
        }}
      >
        {OverviewCards}
      </StyledCarousel>
    </MobileCTA>
  );
};

const MobileCTA = styled.div.attrs({
  className: 'mb-5 -mx-2 tablet:-mx-3 desktop:mx-0',
})``;
